/**
 * OpenAI Integration — generate Design JSON V2 from an approved Creative Plan.
 *
 * Build the request deterministically → send it through the abstract provider →
 * parse the structured output → enforce the Design JSON V2 schema with the
 * Phase 2 validator.
 *
 * The model translates intent into a document. It makes no creative decisions:
 * they were made by the Thinking Engine and approved by the Art Director.
 */

import { validateDesignDocument } from '../validator';
import { SCHEMA_ID } from '../schema';
import { buildDesignRequest } from './buildDesignRequest';
import { createBackendProvider, isDesignProvider, DesignProviderError } from './provider';

/**
 * Extract the documents from the model's JSON, tolerant of the shapes a model
 * reasonably returns, strict about everything after that.
 *
 * @param {(string|Object)} raw
 * @returns {Object[]}
 * @throws {DesignProviderError} when the response is not usable JSON.
 */
export const parseDesignDocuments = (raw) => {
  let data;
  try {
    data = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (err) {
    throw new DesignProviderError('The designer returned invalid JSON.', { retryable: true, cause: err });
  }
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.documents)) return data.documents;
  if (data && typeof data === 'object' && data.schema === SCHEMA_ID) return [data];
  throw new DesignProviderError('The designer response contained no Design JSON V2 documents.', { retryable: true });
};

/**
 * Generate Design JSON V2 documents.
 *
 * @param {Object} inputs
 * @param {Object} inputs.plan       the APPROVED Creative Plan.
 * @param {Object} inputs.research   Research Brief.
 * @param {Object} inputs.genome     Style Genome.
 * @param {Object[]} inputs.patterns selected Library Patterns.
 * @param {Object} [options]
 * @param {Object} [options.provider]  a DesignProvider (defaults to the backend proxy).
 * @param {string} [options.providerName]
 * @param {number} [options.count]
 * @param {AbortSignal} [options.signal]
 * @param {Function} [options.onDelta]
 * @returns {Promise<Object>} { documents, valid, results, raw, request, meta }
 * @throws {DesignProviderError} on transport failure or unusable output.
 */
export const generateDesignDocuments = async (inputs = {}, options = {}) => {
  const provider = isDesignProvider(options.provider)
    ? options.provider
    : createBackendProvider(options.providerName || 'openai', options);

  const request = buildDesignRequest(inputs, { ...options, provider: provider.provider });
  const raw = await provider.generate(request, { signal: options.signal, onDelta: options.onDelta });

  const documents = parseDesignDocuments(raw);
  // The schema is enforced here, not trusted: structured output shapes the
  // response, the validator decides whether it is acceptable.
  const results = documents.map((document) => ({ document, validation: validateDesignDocument(document) }));

  return {
    documents,
    results,
    valid: results.every((r) => r.validation.valid),
    raw,
    request,
    meta: {
      provider: provider.provider,
      providerId: provider.id,
      // The model is the server's choice and is not reported back through the
      // raw-string contract; claiming one here would be a guess.
      count: request.count,
      requested: request.count,
      returned: documents.length,
    },
  };
};
